import type { ChannelMessage } from './queries';

type AttachmentMessage = Pick<
  ChannelMessage,
  'message_type' | 'file_mime_type' | 'file_url' | 'forwarded_from'
>;

export type TeamAttachmentKind = 'image' | 'video' | 'audio' | 'file';

function advertisedTypes(m: AttachmentMessage): string[] {
  return [m.message_type, m.forwarded_from?.original_message_type]
    .map((value) => value?.trim().toLowerCase())
    .filter((value): value is string => Boolean(value));
}

/**
 * Media detection must include Inbox forwards. The team-message row often
 * arrives as the generic `file` type while `forwarded_from` preserves the
 * source type (image/video/audio); relying on `message_type` alone turns
 * forwarded media into a download-only chip.
 */
export function isImageAttachment(m: AttachmentMessage): boolean {
  if (!m.file_url) return false;
  if (advertisedTypes(m).includes('image')) return true;
  if ((m.file_mime_type ?? '').toLowerCase().startsWith('image/')) return true;
  return /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i.test(m.file_url);
}

export function isVideoAttachment(m: AttachmentMessage): boolean {
  if (!m.file_url) return false;
  if (advertisedTypes(m).includes('video')) return true;
  if ((m.file_mime_type ?? '').toLowerCase().startsWith('video/')) return true;
  return /\.(mp4|m4v|mov|webm|ogv)(\?|$)/i.test(m.file_url);
}

/**
 * Attachment-media helpers shared by the three team-chat render surfaces
 * (ChannelView's TeamMsg, DockChatWindow's DockAttachment, ThreadPanel's
 * ThreadAttachment).
 *
 * Context (reporte del owner 2026-07-11): una nota de voz mandada desde otro
 * cliente (móvil) llegó como `message_type:'file'` + `file_url:*.m4a` +
 * `content:"📌 Nota de voz (5s)"` — la web la mostraba como tarjeta de
 * descarga genérica y ADEMÁS repetía el placeholder como burbuja de texto.
 */

/**
 * True when the attachment should render an inline audio player instead of
 * the generic download chip. `message_type === 'audio'` is the canonical
 * signal, but other clients upload voice notes as plain files — sniff the
 * mime and the URL extension as fallbacks (same layering as the image
 * detection in TeamMsg/ThreadAttachment). `webm` is deliberately NOT in the
 * extension list (ambiguous with video) — webm audio must come typed via
 * message_type or an audio/* mime.
 */
export function isAudioAttachment(
  m: AttachmentMessage,
): boolean {
  if (!m.file_url) return false;
  if (advertisedTypes(m).includes('audio')) return true;
  if ((m.file_mime_type ?? '').toLowerCase().startsWith('audio/')) return true;
  return /\.(m4a|mp3|ogg|oga|opus|wav|aac)(\?|$)/i.test(m.file_url);
}

export function getAttachmentKind(m: AttachmentMessage): TeamAttachmentKind {
  if (isImageAttachment(m)) return 'image';
  // Audio wins before video because `.webm` can carry either; an explicit
  // audio MIME/source type is more authoritative than the ambiguous suffix.
  if (isAudioAttachment(m)) return 'audio';
  if (isVideoAttachment(m)) return 'video';
  return 'file';
}

/**
 * True when a message's `content` is just another client's auto-placeholder
 * for its own attachment ("📷 Foto", "📌 Nota de voz (5s)", "Archivo"…).
 * The backend requires `content` min=1 on media sends, so senders without a
 * real caption stuff a human label there; once we render the attachment
 * itself, repeating that label as a text bubble reads as a duplicate. Real
 * captions (anything beyond the known labels) always render.
 */
export function isMediaPlaceholderCaption(content: string | null | undefined): boolean {
  if (!content) return false;
  // Strip leading emoji/pictographs (📷, 📌, 🎤…) + separators, then match
  // the bare label. ️ = variation selector, ‍ = ZWJ.
  const stripped = content
    .trim()
    .replace(/^[\p{Extended_Pictographic}️‍\s:·-]+/u, '')
    .trim();
  return /^(foto|imagen|nota de voz(\s*\(\d+\s*s\))?|audio|video|archivo|documento|adjunto|sticker)$/i.test(
    stripped,
  );
}
